import { mockSectionData, type MockSectionCustomerInput } from './mockSectionData.util';
import { coerceBhConnectionCount } from './bhConnectionCoerce.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE } from './whoWereReachingSnapshotDetect.util';

/** Ranked diagnosis row for BH / medical breakdown charts. */
export interface BhMedicalDiagnosisRow {
  label: string;
  count: number;
  pct: number;
}

/** Max ranked diagnosis rows shown in each BH / medical table. */
export const BH_MEDICAL_DIAGNOSES_TOP_COUNT = 6;

/**
 * Returns the highest-count diagnosis rows for display.
 * @param rows - Ranked diagnosis rows (any order).
 * @returns At most {@link BH_MEDICAL_DIAGNOSES_TOP_COUNT} rows, highest count first.
 */
export const takeTopBhMedicalDiagnosisRows = (
  rows: BhMedicalDiagnosisRow[],
): BhMedicalDiagnosisRow[] =>
  [...rows]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, BH_MEDICAL_DIAGNOSES_TOP_COUNT);

const DX_TYPE_BH = 'Behavioral Health Diagnosis';
const DX_TYPE_MEDICAL = 'Medical Diagnosis';

/**
 * Lowercases warehouse row keys so BigQuery column casing differences still map.
 * @param row - Raw warehouse row.
 * @returns Row with lowercase keys.
 */
export const normalizeWarehouseDxRecord = (
  row: Record<string, unknown>,
): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    normalized[key.toLowerCase()] = value;
  }
  return normalized;
};

const readDxType = (row: Record<string, unknown>): unknown =>
  row.type ?? row.TYPE ?? row.dx_type ?? row.diagnosis_type ?? row.dx_category;

/**
 * Returns true when a warehouse dx row is behavioral health.
 * @param type - Warehouse `type` column value.
 */
export const isBhMedicalDiagnosisType = (type: unknown): boolean => {
  const normalized = String(type ?? '')
    .trim()
    .toLowerCase();
  return (
    normalized === DX_TYPE_BH.toLowerCase() ||
    normalized.includes('behavioral health')
  );
};

/**
 * Returns true when a warehouse dx row is medical (non-BH).
 * @param type - Warehouse `type` column value.
 */
export const isMedicalDiagnosisType = (type: unknown): boolean => {
  const normalized = String(type ?? '')
    .trim()
    .toLowerCase();
  if (isBhMedicalDiagnosisType(type)) {
    return false;
  }
  return (
    normalized === DX_TYPE_MEDICAL.toLowerCase() || normalized.includes('medical')
  );
};

/**
 * Splits warehouse dx rows into BH and medical cohorts.
 * @param rows - Raw warehouse dx rows for one customer/period.
 */
export const partitionBhMedicalDxWarehouseRows = (
  rows: Array<Record<string, unknown>>,
): { bh: Array<Record<string, unknown>>; medical: Array<Record<string, unknown>> } => {
  const normalizedRows = rows.map(row => normalizeWarehouseDxRecord(row));
  return {
    bh: normalizedRows.filter(row => isBhMedicalDiagnosisType(readDxType(row))),
    medical: normalizedRows.filter(row =>
      isMedicalDiagnosisType(readDxType(row)),
    ),
  };
};

const MEDICAL_CONDITION_ACRONYMS = new Set(['ptsd', 'copd', 'tia', 'ckd']);

/** Studio-style labels for warehouse condition slugs with slashes or special casing. */
const WAREHOUSE_CONDITION_LABEL_OVERRIDES: Record<string, string> = {
  asthma_copd: 'Asthma / COPD',
  stroke_tia: 'Stroke / TIA',
  ckd_renal: 'CKD / renal',
};

/**
 * Formats a warehouse `condition` slug (e.g. `blood_disorders`) for report display.
 * @param raw - Raw condition value from BigQuery.
 * @returns Human-readable diagnosis label.
 */
export const formatWarehouseDiagnosisConditionLabel = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return 'Unknown';
  }

  const slug = trimmed.toLowerCase();
  const override = WAREHOUSE_CONDITION_LABEL_OVERRIDES[slug];
  if (override) {
    return override;
  }

  if (!slug.includes('_')) {
    if (MEDICAL_CONDITION_ACRONYMS.has(slug)) {
      return slug.toUpperCase();
    }
    return slug.charAt(0).toUpperCase() + slug.slice(1);
  }

  return slug
    .split('_')
    .filter(Boolean)
    .map(word => {
      if (MEDICAL_CONDITION_ACRONYMS.has(word)) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const formatDxDisplayLabel = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return 'Unknown';
  }
  if (!/^[a-z0-9_]+$/.test(trimmed)) {
    return trimmed;
  }
  return formatWarehouseDiagnosisConditionLabel(trimmed);
};

const readDxLabel = (row: Record<string, unknown>): string => {
  const candidates = [
    row.condition,
    row.diagnosis,
    row.dx,
    row.category,
    row.name,
    row.label,
    row.diagnosis_name,
    row.dx_name,
    row.DIAGNOSIS,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return formatDxDisplayLabel(candidate);
    }
  }
  return 'Unknown';
};

const readDxCount = (row: Record<string, unknown>): number => {
  const candidates = [
    row.count,
    row.counts,
    row.COUNT,
    row.ct,
    row.ct_dx,
    row.ct_diagnosis,
    row.diagnosis_count,
    row.member_count,
    row.n_members,
    row.num,
    row.n,
  ];
  for (const candidate of candidates) {
    const value = coerceBhConnectionCount(candidate);
    if (value > 0) {
      return value;
    }
  }

  const yearMo = coerceBhConnectionCount(row.year_mo);
  for (const [key, value] of Object.entries(row)) {
    if (key === 'year_mo' || key === 'customer') {
      continue;
    }
    if (!/count|ct|num|n_|members?/i.test(key)) {
      continue;
    }
    const parsed = coerceBhConnectionCount(value);
    if (parsed > 0 && parsed !== yearMo) {
      return parsed;
    }
  }

  return 0;
};

const toNormalizedDxRows = (
  rows: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> =>
  rows.map(row => normalizeWarehouseDxRecord(row));

/**
 * Coerces ranked diagnosis rows from snapshot JSON or raw warehouse rows.
 * @param rows - Snapshot `breakdown` / `medical` array or warehouse dx rows.
 * @returns Ranked rows with counts and percentages, limited to the top 6 by count.
 */
export const coerceBhMedicalDiagnosisRows = (
  rows: unknown,
): BhMedicalDiagnosisRow[] => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const first = rows[0];
  if (
    first &&
    typeof first === 'object' &&
    'label' in first &&
    'count' in first
  ) {
    const mapped = rows
      .map(row => {
        if (!row || typeof row !== 'object') {
          return null;
        }
        const record = row as { label?: unknown; count?: unknown };
        const label = typeof record.label === 'string' ? record.label.trim() : '';
        const count = coerceBhConnectionCount(record.count);
        if (!label || count <= 0) {
          return null;
        }
        return { label, count };
      })
      .filter((row): row is { label: string; count: number } => row !== null);

    const total = mapped.reduce((sum, row) => sum + row.count, 0);
    if (total <= 0) {
      return [];
    }

    return takeTopBhMedicalDiagnosisRows(
      mapped
        .map(row => ({
          ...row,
          pct: Math.round((row.count / total) * 100),
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    );
  }

  return takeTopBhMedicalDiagnosisRows(
    buildBhMedicalDiagnosisBreakdown(rows as Array<Record<string, unknown>>),
  );
};

/**
 * Converts warehouse diagnosis rows to ranked breakdown rows with percentages.
 * @param rows - Raw warehouse dx rows.
 * @returns Breakdown rows sorted by count descending.
 */
export const buildBhMedicalDiagnosisBreakdown = (
  rows: Array<Record<string, unknown>>,
): BhMedicalDiagnosisRow[] => {
  const mapped = toNormalizedDxRows(rows)
    .map(row => ({
      label: readDxLabel(row),
      count: readDxCount(row),
    }))
    .filter(row => row.count > 0);

  const total = mapped.reduce((sum, row) => sum + row.count, 0);
  if (total <= 0) {
    return [];
  }

  return mapped
    .map(row => ({
      ...row,
      pct: Math.round((row.count / total) * 100),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

/**
 * Builds the bh-medical-diagnoses snapshot from warehouse dx and connection rows.
 * @param bhDxRows - Behavioral-health diagnosis rows.
 * @param medicalDxRows - Medical diagnosis rows.
 * @param connectionRow - BH connection aggregate row for headline totals.
 * @param yearMo - Six-digit period key.
 * @param customer - Report customer context.
 * @param reportingPeriod - Human-readable report period label.
 * @returns Snapshot object or null when warehouse data is empty.
 */
export const buildBhMedicalDiagnosesSnapshotFromRows = (
  bhDxRows: Array<Record<string, unknown>>,
  medicalDxRows: Array<Record<string, unknown>>,
  connectionRow: {
    total_new_bh_dx?: unknown;
    total_new_medical_dx?: unknown;
    total_new_dx?: unknown;
  },
  yearMo: string,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> | null => {
  const rankedBh = buildBhMedicalDiagnosisBreakdown(bhDxRows);
  const rankedMedical = buildBhMedicalDiagnosisBreakdown(medicalDxRows);
  const breakdownTotal = rankedBh.reduce((sum, row) => sum + row.count, 0);
  const medicalTotal = rankedMedical.reduce((sum, row) => sum + row.count, 0);
  const breakdown = takeTopBhMedicalDiagnosisRows(rankedBh);
  const medical = takeTopBhMedicalDiagnosisRows(rankedMedical);
  const totalNewBh =
    coerceBhConnectionCount(connectionRow.total_new_bh_dx) || breakdownTotal;
  const totalNewMedical =
    coerceBhConnectionCount(connectionRow.total_new_medical_dx) || medicalTotal;
  const totalNewDx = coerceBhConnectionCount(connectionRow.total_new_dx);

  if (
    breakdown.length === 0 &&
    medical.length === 0 &&
    totalNewBh <= 0 &&
    totalNewMedical <= 0
  ) {
    return null;
  }

  const mockBase = mockSectionData(
    'bh-medical-diagnoses',
    customer,
    reportingPeriod,
  );

  const fieldSources: ReportSectionFieldSources = {
    totalNewBhDx: 'warehouse',
    totalNewMedicalDx: 'warehouse',
    breakdown: 'warehouse',
    medical: 'warehouse',
    whyDenominator: 'mock',
    insights: 'mock',
  };

  return {
    ...mockBase,
    snapshotSource: WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
    yearMo,
    breakdown,
    medical,
    totalNewBhDx: totalNewBh,
    totalNewMedicalDx: totalNewMedical,
    totalNewDx: totalNewDx > 0 ? totalNewDx : totalNewBh + totalNewMedical,
    dxTypes: { bh: DX_TYPE_BH, medical: DX_TYPE_MEDICAL },
    fieldSources,
  };
};
